import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { ArrowRight, BookOpen, Sparkles, Feather, Lock } from "lucide-react";
import { PackHighlight } from "@/components/PackHighlight";
import { CoffretHighlight } from "@/components/CoffretHighlight";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const fmtPrice = (cents) => (cents ? (cents / 100).toFixed(2).replace(".", ",") + " €" : null);
const cardImg = (n) => `${BACKEND}/api/game/ile/image/${n}`;

export default function IleCarrefour() {
  const [prices, setPrices] = useState({});
  const [ileAdvId, setIleAdvId] = useState(null);

  useEffect(() => {
    api.get("/products").then((r) => {
      const map = {};
      (r.data || []).forEach((p) => { map[p.key] = p.active && p.price_cents ? p.price_cents : null; });
      setPrices(map);
    }).catch(() => {});
    api.get("/adventure/books").then((r) => {
      const ile = (r.data || []).find((b) => b.slug === "ile-de-labondance");
      if (ile) setIleAdvId(ile.id);
    }).catch(() => {});
  }, []);

  const experiences = [
    {
      key: "aventure", n: 1, icon: BookOpen, accent: "#385E3A",
      name: "L'Aventure", positioning: "Je vis l'histoire.",
      desc: "Une aventure interactive en 21 étapes, avec choix, personnages, objets et plusieurs chemins.",
      price: fmtPrice(prices.aventure),
      subLabel: "",
      cta: "Vivre l'Aventure",
      to: ileAdvId ? `/aventure/${ileAdvId}` : null,
      loading: !ileAdvId,
      available: true,
    },
    {
      key: "jeu", n: 2, icon: Sparkles, accent: "#2E5B7F",
      name: "Le Jeu", positioning: "Je joue et j'explore.",
      desc: "21 cartes illustrées. Cartes 01 à 03 accessibles gratuitement, 04 à 21 en accès premium.",
      note: "",
      price: fmtPrice(prices.jeu ?? 990),
      subLabel: "3 cartes offertes",
      cta: "Jouer",
      to: "/jeu/ile",
      loading: false,
      available: true,
    },
    {
      key: "carnet", n: 3, icon: Feather, accent: "#A63B36",
      name: "Le Carnet", positioning: "Je réfléchis et j'approfondis.",
      desc: "Des carnets de 21 jours, en PDF téléchargeable ou en livre broché.",
      note: "",
      price: null,
      subLabel: "Dès 9,90 €",
      cta: "Découvrir les Carnets",
      to: "/boutique/carnets",
      loading: false,
      available: true,
    },
  ];

  return (
    <div>
      {/* Hero */}
      <section data-testid="ile-carrefour-hero" className="relative overflow-hidden bg-[#1F2A24] text-[#F6F2E9]">
        <div className="absolute inset-0 opacity-25" style={{ backgroundImage: `url(${cardImg(1)})`, backgroundSize: "cover", backgroundPosition: "center", filter: "blur(4px)" }} />
        <div className="absolute inset-0 bg-gradient-to-b from-[#1F2A24]/75 via-[#1F2A24]/85 to-[#1F2A24]" />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-8 py-20 md:py-28 text-center">
          <Link to="/univers" data-testid="carrefour-back-univers" className="inline-block font-sans text-xs uppercase tracking-[0.3em] text-[#C9A24B] font-semibold hover:text-white transition-colors">
            Les Univers — Premier univers
          </Link>
          <h1 className="font-serif text-4xl md:text-6xl mt-4">L'Île de l'Abondance</h1>
          <div className="gold-divider w-24 mx-auto my-6" />
          <p className="font-serif italic text-lg md:text-xl text-[#F6F2E9]/90">Un univers. Trois expériences.</p>
          <p className="font-sans text-xs tracking-[0.2em] text-[#C9A24B] mt-4">CORPS • CŒUR • CERVEAU • CONSCIENCE</p>
        </div>
      </section>

      {/* Three experiences */}
      <section className="max-w-7xl mx-auto px-4 sm:px-8 py-14 md:py-20">
        <div className="grid md:grid-cols-3 gap-6" data-testid="carrefour-experiences">
          {experiences.map((e) => {
            const Icon = e.icon;
            return (
              <div key={e.key} data-testid={`carrefour-${e.key}`} className="rounded-2xl bg-white border border-[#E8DCC4] overflow-hidden flex flex-col">
                <div className="aspect-[16/10] relative overflow-hidden" style={{ background: `linear-gradient(150deg, ${e.accent}, ${e.accent}cc)` }}>
                  {e.available && (
                    <img src={cardImg(e.n)} alt={e.name} loading="lazy" className="absolute inset-0 w-full h-full object-cover opacity-90" />
                  )}
                  <div className="absolute inset-0" style={{ background: "linear-gradient(to top, rgba(31,42,36,0.85), rgba(31,42,36,0.15))" }} />
                  <div className="relative h-full flex flex-col justify-between p-5">
                    <span className="inline-flex items-center gap-1.5 self-start rounded-full bg-black/30 backdrop-blur px-3 py-1.5">
                      <Icon size={14} className="text-[#C9A24B]" />
                      <span className="font-ui text-[11px] tracking-widest text-white">{String(e.n).padStart(2, "0")}</span>
                    </span>
                    <div>
                      <p className="font-serif text-2xl text-white drop-shadow">{e.name}</p>
                      <p className="font-serif italic text-white/85">{e.positioning}</p>
                    </div>
                  </div>
                </div>
                <div className="p-6 flex flex-col flex-1">
                  <p className="font-sans text-sm text-[#1F2A24]/75 flex-1 leading-relaxed">{e.desc}</p>
                  {e.note && (
                    <span className="inline-flex self-start items-center gap-1.5 rounded-full bg-[#C9A24B]/15 text-[#8a6d2a] px-3 py-1 font-ui text-[11px] tracking-wide mt-3">
                      <Sparkles size={12} /> {e.note}
                    </span>
                  )}
                  <div className="mt-5 flex items-center justify-between gap-3">
                    {e.price ? (
                      <span data-testid={`carrefour-price-${e.key}`} className="font-serif text-xl text-[#1F2A24]">{e.price}</span>
                    ) : (
                      <span className="font-sans text-xs tracking-wider text-[#1F2A24]/40">{e.subLabel}</span>
                    )}
                    {e.to ? (
                      <Link to={e.to} data-testid={`carrefour-cta-${e.key}`} className="inline-flex items-center gap-2 rounded-full text-white px-5 py-2.5 font-ui text-sm transition-colors" style={{ backgroundColor: e.accent }}>
                        {e.cta} <ArrowRight size={15} />
                      </Link>
                    ) : e.loading ? (
                      <span data-testid={`carrefour-cta-${e.key}`} className="inline-flex items-center gap-2 rounded-full text-white/80 px-5 py-2.5 font-ui text-sm opacity-60 cursor-wait" style={{ backgroundColor: e.accent }}>
                        Chargement…
                      </span>
                    ) : (
                      <span data-testid={`carrefour-cta-${e.key}`} className="inline-flex items-center gap-2 rounded-full bg-[#E8DCC4] text-[#1F2A24]/50 px-5 py-2.5 font-ui text-sm cursor-not-allowed">
                        <Lock size={14} /> {e.cta}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Complementarity note */}
        <p className="text-center font-serif italic text-[#1F2A24]/60 max-w-2xl mx-auto mt-10">
          Trois expériences distinctes et complémentaires : je vis l'histoire, je joue et j'explore, je réfléchis et j'approfondis.
        </p>

        <div className="mt-10 -mx-4 sm:mx-0 sm:rounded-3xl overflow-hidden" data-testid="carrefour-offres">
          <PackHighlight />
          <CoffretHighlight />
        </div>
      </section>
    </div>
  );
}
